import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { useAuthStore } from './auth'
import { usePlayerStore } from './player'

function apiClient() {
  const auth = useAuthStore()
  return axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: auth.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : {}
  })
}

// Map persisted event type → in-memory notification type (for read coloring)
const TYPE_MAP = {
  CONTRACT_COMPLETED:            'success',
  SCHEDULED_ARRIVAL:             'success',
  SCHEDULED_DEPARTURE:           'info',
  SCHEDULED_DEPARTURE_MISSED:    'warning',
  SCHEDULED_DEPARTURE_PREEMPTED: 'info',
  SCHEDULED_ROUTE_RESUMED:       'info',
  ASSIGNMENT_CANCELLED:          'danger',
  SCENARIO_STARTED:              'accent',
  SCENARIO_COMPLETED:            'warning',
}

export const useNotificationStore = defineStore('notifications', () => {
  const items             = ref([])   // full archive — always all scenarios
  const toasts            = ref([])   // IDs of notifications currently shown as toasts
  const currentScenarioId = ref(null) // set while a WS session is active

  // Notification scope from persisted player profile (null → treat as 'ALL')
  const notificationScope = computed(() =>
    usePlayerStore().profile?.settings?.notificationScope ?? 'ALL'
  )

  // Subset of items shown in the bell and as toasts — respects scope setting
  const bellItems = computed(() => {
    if (notificationScope.value === 'CURRENT') {
      return items.value.filter(n => !n.scenarioId || n.scenarioId === currentScenarioId.value)
    }
    return items.value
  })

  // Unread count for the full archive page (NotificationsView)
  const unreadCount = computed(() => items.value.filter(n => !n.read).length)
  // Unread count for the bell badge — respects scope
  const bellUnreadCount = computed(() => bellItems.value.filter(n => !n.read).length)

  const activeToasts = computed(() => {
    const bellIds = new Set(bellItems.value.map(n => n.id))
    return toasts.value
      .map(id => items.value.find(n => n.id === id))
      .filter(n => n && bellIds.has(n.id))
  })

  function setCurrentScenario(scenarioId) {
    currentScenarioId.value = scenarioId
  }

  function push({ id = null, type = 'info', title, body = null, silent = false, scenarioId = null }) {
    const itemId = id || (Date.now().toString(36) + Math.random().toString(36).slice(2))
    const item = { id: itemId, type, title, body, read: false, ts: new Date(), scenarioId }
    items.value.unshift(item)
    if (items.value.length > 100) items.value.pop()
    if (!silent) toasts.value.push(itemId)
    return itemId
  }

  function dismissToast(id) {
    const idx = toasts.value.indexOf(id)
    if (idx !== -1) toasts.value.splice(idx, 1)
  }

  function markRead(id) {
    const item = items.value.find(n => n.id === id)
    if (item) item.read = true
  }

  function markAllRead() {
    items.value.forEach(n => { n.read = true })
  }

  async function markAllReadRemote(scenarioId) {
    markAllRead()
    await apiClient().post(`/scenarios/${scenarioId}/me/notifications/mark-read`)
  }

  async function fetchForScenario(scenarioId) {
    const res = await apiClient().get(`/scenarios/${scenarioId}/me/notifications`)
    const existing = new Set(items.value.map(n => n.id))
    const fetched = res.data
      .filter(n => !existing.has(n.id))
      .map(n => ({
        id:    n.id,
        type:  TYPE_MAP[n.type] || 'info',
        title: n.title,
        body:  n.body,
        read:  !!n.readAt,
        ts:    new Date(n.createdAt),
        scenarioId,
        persisted: true,
      }))
    items.value = [...items.value, ...fetched].sort((a, b) => b.ts - a.ts).slice(0, 200)
  }

  function clear() {
    items.value = []
    toasts.value = []
  }

  return { items, bellItems, unreadCount, bellUnreadCount, activeToasts, notificationScope, setCurrentScenario, push, dismissToast, markRead, markAllRead, markAllReadRemote, fetchForScenario, clear }
})
